import { emsBackgroundYellow, emsWarningYellow } from "@electricMotorShop/EmsVariables";
import { getCartLineProductMessages, getCartMessage } from "@electricMotorShop/Store/Data/Carts/XcCartsSelector";
import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { CartLineCardCondensedStyles } from "@insite/content-library/Widgets/Cart/CartLineCardCondensed";
import CartLineCardExpanded, {
    CartLineCardExpandedStyles,
} from "@insite/content-library/Widgets/Cart/CartLineCardExpanded";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// KK: 2/23/24 --- Replaced cart icon with image picker field

const enum fields {
    showLineNotes = "showLineNotes",
    showImage = "showImage",
    showProductTitle = "showProductTitle",
    showBrand = "showBrand",
    showRemoveAllActions = "showRemoveAllActions",
    showPerUnitPrice = "showPerUnitPrice",
    showPriceLabel = "showPriceLabel",
    showAddToList = "showAddToList",
    showItemCount = "showItemCount",
    showProductNumber = "showProductNumber",
    showBackToTop = "showBackToTop",
    showVmiBinDetails = "showVmiBinDetails",
    emptyCartImage = "emptyCartImage",
    customCSS = "customCSS",
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.showImage]: boolean;
        [fields.showProductTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showRemoveAllActions]: boolean;
        [fields.showPerUnitPrice]: boolean;
        [fields.showPriceLabel]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showItemCount]: boolean;
        [fields.showProductNumber]: boolean;
        [fields.showBackToTop]: boolean;
        [fields.showVmiBinDetails]: boolean;
        [fields.emptyCartImage]: string;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
    isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
    showPricing: state.context.showPricing,
    showInventory: state.context.showInventory,
});

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    image?: LazyImageProps;
    centeringImageWrapper?: InjectableCss;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    headerWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
        `,
    },
    header: {
        condensedViewCheckboxGroup: {
            css: css`
                margin-left: auto;
            `,
        },
        condensedViewCheckbox: {
            typographyProps: {
                css: css`
                    margin-left: 10px;
                `,
            },
        },
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    margin-left: 20px;
                `,
            },
        },
        removeAllLink: {
            css: css`
                margin-left: 24px;
            `,
        },
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        warningIcon: {
            src: "AlertCircle",
            color: emsWarningYellow,
            css: css`
                margin-right: 10px;
                padding-bottom: 2px;
            `,
        },
        warningSection: {
            css: css`
                display: flex;
                align-items: center;
                border: 2px solid ${emsWarningYellow};
                border-radius: 4px;
                background: ${emsBackgroundYellow};
                padding: 15px 10px;
                margin-bottom: 5px;
            `,
        },
    },
    footerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    backToTopButton: {
        variant: "secondary",
        css: css`
            margin: 16px 0;
        `,
    },
    imageWrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    image: {
        css: css`
            width: 100%;
        `,
    },
    img: {
        css: css`
            width: 100%;
        `,
    },
    centeringImageWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 430px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    cart,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields,
    isClearingCart,
    isUpdatingCartLine,
    isLoading,
    updateCartLine,
    removeCartLine,
    isRemovingCartLine,
    showPricing,
    showInventory,
}) => {
    const [isCondensed, setIsCondensed] = useState(false);
    const [isRemoveAllModalOpen, setIsRemoveAllModalOpen] = useState(false);
    const isEager = useEagerLogoLoading();

    if (!cart || (cart.lineCount && !cart.cartLines) || isClearingCart) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    const cartLines = cart.cartLines || [];

    if (cartLines.length === 0) {
        return (
            <StyledSection {...styles.centeringImageWrapper} className="centeringWrapper">
                <StyledWrapper {...styles.imageWrapper} className="imageWrapper">
                    <Img
                        src={fields.emptyCartImage}
                        loading={isEager ? "eager" : "lazy"}
                        altText={translate("Home")}
                        {...styles.img}
                        className="img"
                    />
                </StyledWrapper>
                <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                    {siteMessage("Cart_NoOrderLines")}
                </Typography>
            </StyledSection>
        );
    }

    const isCondensedChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensed(value);
    };

    const removeAllClickHandler = () => {
        if (cart.status === "AwaitingApproval") {
            setIsRemoveAllModalOpen(true);
        } else {
            clearCurrentCart();
        }
    };

    const submitRemoveAllClickHandler = () => {
        setIsRemoveAllModalOpen(false);
        clearCurrentCart();
    };

    const setCartLineMessage = (products: string[], message: string, cartLine: CartLineModel) => {
        if (products) {
            const product = products.filter(x => x === cartLine.erpNumber);
            if (product.length > 0) {
                const messages = cartLine.properties["messages"];
                cartLine.properties["messages"] = messages ? messages.concat(" | ", message) : message;
            }
        }
    };

    // Cart Header Messages from backend
    const cartMessages = [];

    // Credit Hold/Limit messages
    const creditHoldLimitMessage = getCartMessage(cart.messages, "Credit:");
    if (creditHoldLimitMessage) {
        cartMessages.push(creditHoldLimitMessage);
    }

    // Territory Restriction messages
    const territoryRestrictionBadZipCodeMessage = getCartMessage(cart.messages, "TR:");
    const territoryRestrictionDefaultMessage = getCartMessage(cart.messages, "TRDM:");
    const territoryRestrictionMessage =
        territoryRestrictionDefaultMessage && territoryRestrictionDefaultMessage.length > 0
            ? territoryRestrictionDefaultMessage
            : territoryRestrictionBadZipCodeMessage;
    if (territoryRestrictionMessage) {
        cartMessages.push(territoryRestrictionMessage);
    }

    // // Inventory messages
    const inventoryCartMessage = getCartMessage(cart.messages, "INVM:");
    if (inventoryCartMessage) {
        cartMessages.push(inventoryCartMessage);
    }

    // Cart Line Messages from backend
    const territoryRestrProductsMsg = getCartMessage(cart.messages, "TRPM:");
    const restrictedProducts = getCartLineProductMessages(cart.messages, "TRP:");

    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const isLoadingCart =
        isLoading[cart.id] || (cart.status === "AlternateCart" && isLoading[API_URL_CURRENT_FRAGMENT]);

    const cartLinesDisplay = cartLines.map(cartLine => {
        const mappedLine = { ...cartLine };

        mappedLine.properties = { ...mappedLine.properties };

        setCartLineMessage(restrictedProducts, territoryRestrProductsMsg, mappedLine);

        if (!showPricing && cartLine.isPromotionItem) {
            return;
        }

        const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === cartLine.id) : [];
        const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
        return (
            <CartLineContext.Provider value={mappedLine} key={mappedLine.id}>
                <CartLineCardExpanded
                    cart={cart}
                    promotions={matchingPromotions}
                    productSettings={productSettings}
                    showInventoryAvailability={showInventory}
                    showLineNotes={fields.showLineNotes}
                    showImage={fields.showImage}
                    showProductTitle={fields.showProductTitle}
                    showBrand={fields.showBrand}
                    showPerUnitPrice={fields.showPerUnitPrice}
                    showPriceLabel={fields.showPriceLabel}
                    showAddToList={fields.showAddToList}
                    showProductNumber={fields.showProductNumber}
                    extendedStyles={styles.cardExpanded}
                    updateCartLine={updateCartLine}
                    removeCartLine={removeCartLine}
                    showRemoveAction={showRemoveAction}
                    showVmiBinDetails={fields.showVmiBinDetails}
                />
            </CartLineContext.Provider>
        );
    });

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledSection {...styles.wrapper}>
                <LoadingOverlay
                    {...styles.loadingOverlay}
                    loading={isLoadingCart || isUpdatingCartLine || !promotionsDataView.value || isRemovingCartLine}
                >
                    <CartLinesHeader
                        totalItemCount={cartLines.length}
                        isCondensed={isCondensed}
                        productsCannotBePurchased={productsCannotBePurchased}
                        cartNotPriced={cart.cartNotPriced}
                        hasInsufficientInventory={cart.hasInsufficientInventory}
                        hasInvalidPrice={hasProductsWithInvalidPrice(cart)}
                        cartMessages={cartMessages}
                        failedToGetRealTimeInventory={cart.failedToGetRealTimeInventory}
                        onIsCondensedChange={isCondensedChangeHandler}
                        onRemoveAllClick={removeAllClickHandler}
                        showItemCount={fields.showItemCount}
                        showRemoveAllActions={fields.showRemoveAllActions}
                        fulfillmentMethod={cart.fulfillmentMethod}
                    />
                    {cartLinesDisplay}
                    {fields.showBackToTop !== false && <CartLinesFooter />}
                    <TwoButtonModal
                        headlineText={translate("Are you sure?")}
                        modalIsOpen={isRemoveAllModalOpen}
                        messageText={siteMessage("OrderApproval_RemoveAllConfirmationMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Clear cart and delete order")}
                        onCancel={() => setIsRemoveAllModalOpen(false)}
                        onSubmit={submitRemoveAllClickHandler}
                    ></TwoButtonModal>
                </LoadingOverlay>
            </StyledSection>
        </StyledWrapper>
    );
};

interface CartLinesHeaderProps {
    totalItemCount: number;
    isCondensed: boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    hasInsufficientInventory?: boolean;
    hasInvalidPrice?: boolean;
    failedToGetRealTimeInventory?: boolean;
    cartMessages?: string[];
    onIsCondensedChange: CheckboxProps["onChange"];
    onRemoveAllClick: () => void;
    showItemCount?: boolean;
    showRemoveAllActions?: boolean;
    fulfillmentMethod?: string;
}

const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    totalItemCount,
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    cartMessages,
    onRemoveAllClick,
    showItemCount = true,
    showRemoveAllActions = true,
    fulfillmentMethod,
}) => {
    const headerStyles = styles.header || {};

    return (
        <StyledSection {...styles.headerWrapper} id="cartHeader">
            <StyledSection {...headerStyles.mainSection}>
                {showItemCount && (
                    <Typography {...headerStyles.itemCountText}>{`${totalItemCount} ${translate("Items")}`}</Typography>
                )}

                {showRemoveAllActions && (
                    <Hidden above="md">
                        <OverflowMenu position="end" {...headerStyles.overflowMenu}>
                            <Clickable onClick={onRemoveAllClick}>{translate("Remove All")}</Clickable>
                        </OverflowMenu>
                    </Hidden>
                )}
                {showRemoveAllActions && (
                    <Hidden below="lg">
                        <Link
                            onClick={onRemoveAllClick}
                            {...headerStyles.removeAllLink}
                            data-test-selector="cartlineHeader_removeAll"
                        >
                            {translate("Remove All")}
                        </Link>
                    </Hidden>
                )}
            </StyledSection>
            {productsCannotBePurchased && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                    >
                        {siteMessage("Cart_ProductsCannotBePurchased")}
                    </Typography>
                </StyledSection>
            )}
            {cartNotPriced && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                    >
                        {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {hasInsufficientInventory && fulfillmentMethod === "Ship" && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                    >
                        {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {hasInsufficientInventory && fulfillmentMethod === "PickUp" && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningInsufficientPickupInventory"
                    >
                        {siteMessage("Cart_InsufficientPickupInventory")}
                    </Typography>
                </StyledSection>
            )}
            {/* XCentium Addition for Cart Messages */}
            {cartMessages &&
                cartMessages.map((cartMessage, i) => {
                    return (
                        <StyledSection {...headerStyles.warningSection} key={`cm_${i}`}>
                            <Icon {...headerStyles.warningIcon}></Icon>
                            <Typography
                                {...headerStyles.warningText}
                                data-test-selector="cartlineHeader_warningCreditHoldOrLimitAtCheckout"
                            >
                                {cartMessage}
                            </Typography>
                        </StyledSection>
                    );
                })}
            {!cartNotPriced && hasInvalidPrice && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>{siteMessage("Cart_InvalidPriceAtCheckout")}</Typography>
                </StyledSection>
            )}
            {failedToGetRealTimeInventory && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>
                        {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
        </StyledSection>
    );
};

const CartLinesFooter: FC = () => {
    const backToTopClickHandler = () => {
        const header = document.getElementById("cartHeader");
        if (header) {
            window.scrollTo(0, header.offsetTop);
        }
    };

    return (
        <StyledSection {...styles.footerWrapper}>
            <Button onClick={backToTopClickHandler} {...styles.backToTopButton}>
                {translate("Back to Top")}
            </Button>
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
                tab: basicTab,
                sortOrder: 0,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.showProductTitle,
                displayName: "Product title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 3,
            },
            {
                name: fields.showRemoveAllActions,
                displayName: "Remove all actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 4,
            },
            {
                name: fields.showPerUnitPrice,
                displayName: "Per unit price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 5,
            },
            {
                name: fields.showPriceLabel,
                displayName: "Price label",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 6,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to list",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 7,
            },
            {
                name: fields.showItemCount,
                displayName: "Item count",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 8,
            },
            {
                name: fields.showProductNumber,
                displayName: "Product number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 9,
            },
            {
                name: fields.showBackToTop,
                displayName: "Back to top",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 10,
            },
            {
                name: fields.showVmiBinDetails,
                displayName: "VMI Bin details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 11,
            },
            {
                name: fields.emptyCartImage,
                displayName: "Empty Cart Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
                sortOrder: 12,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
